import React, { useEffect, useState, useMemo } from "react";
import {
  performanceMonitor,
  mobileOptimizations,
} from "../../utils/performance";

const PerformanceMonitor = React.memo(({ enabled = false }) => {
  const [metrics, setMetrics] = useState({
    loadTime: 0,
    firstPaint: 0,
    firstContentfulPaint: 0,
    largestContentfulPaint: 0,
    cumulativeLayoutShift: 0,
    firstInputDelay: 0,
    deviceInfo: {},
  });

  const [isVisible, setIsVisible] = useState(false);

  // Memoized device information
  const deviceInfo = useMemo(() => {
    return {
      userAgent: navigator.userAgent,
      deviceMemory: navigator.deviceMemory || "Unknown",
      hardwareConcurrency: navigator.hardwareConcurrency || "Unknown",
      connection: navigator.connection
        ? {
            effectiveType: navigator.connection.effectiveType,
            downlink: navigator.connection.downlink,
            rtt: navigator.connection.rtt,
          }
        : "Unknown",
      isLowEnd: mobileOptimizations.isLowEndDevice(),
      isSlowConnection: mobileOptimizations.isSlowConnection(),
      prefersReducedMotion: mobileOptimizations.prefersReducedMotion(),
    };
  }, []);

  useEffect(() => {
    if (!enabled) return;

    // Track page load performance
    const trackLoadPerformance = () => {
      const navigation = performance.getEntriesByType("navigation")[0];
      const paint = performance.getEntriesByType("paint");

      const loadTime = navigation
        ? navigation.loadEventEnd - navigation.loadEventStart
        : 0;
      const firstPaint =
        paint.find((entry) => entry.name === "first-paint")?.startTime || 0;
      const firstContentfulPaint =
        paint.find((entry) => entry.name === "first-contentful-paint")
          ?.startTime || 0;

      setMetrics((prev) => ({
        ...prev,
        loadTime,
        firstPaint,
        firstContentfulPaint,
        deviceInfo,
      }));
    };

    // Track Largest Contentful Paint
    const trackLCP = () => {
      try {
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1];
          setMetrics((prev) => ({
            ...prev,
            largestContentfulPaint: lastEntry.startTime,
          }));
        });

        // Check if the entry type is supported before observing
        if (
          "PerformanceObserver" in window &&
          "supportedEntryTypes" in PerformanceObserver
        ) {
          const supportedTypes = PerformanceObserver.supportedEntryTypes || [];
          if (supportedTypes.includes("largest-contentful-paint")) {
            observer.observe({ entryTypes: ["largest-contentful-paint"] });
          }
        } else {
          // Fallback for older browsers
          observer.observe({ entryTypes: ["largest-contentful-paint"] });
        }
      } catch (error) {
        // Silently ignore unsupported entry types
        console.debug("LCP tracking not supported:", error.message);
      }
    };

    // Track Cumulative Layout Shift
    const trackCLS = () => {
      let clsValue = 0;

      try {
        const observer = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (!entry.hadRecentInput) {
              clsValue += entry.value;
            }
          }
          setMetrics((prev) => ({
            ...prev,
            cumulativeLayoutShift: clsValue,
          }));
        });

        // Check if the entry type is supported before observing
        if (
          "PerformanceObserver" in window &&
          "supportedEntryTypes" in PerformanceObserver
        ) {
          const supportedTypes = PerformanceObserver.supportedEntryTypes || [];
          if (supportedTypes.includes("layout-shift")) {
            observer.observe({ entryTypes: ["layout-shift"] });
          }
        } else {
          // Fallback for older browsers
          observer.observe({ entryTypes: ["layout-shift"] });
        }
      } catch (error) {
        // Silently ignore unsupported entry types
        console.debug("CLS tracking not supported:", error.message);
      }
    };

    // Track First Input Delay
    const trackFID = () => {
      try {
        const observer = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            setMetrics((prev) => ({
              ...prev,
              firstInputDelay: entry.processingStart - entry.startTime,
            }));
          }
        });

        // Check if the entry type is supported before observing
        if (
          "PerformanceObserver" in window &&
          "supportedEntryTypes" in PerformanceObserver
        ) {
          const supportedTypes = PerformanceObserver.supportedEntryTypes || [];
          if (supportedTypes.includes("first-input")) {
            observer.observe({ entryTypes: ["first-input"] });
          }
        } else {
          // Fallback for older browsers
          observer.observe({ entryTypes: ["first-input"] });
        }
      } catch (error) {
        // Silently ignore unsupported entry types
        console.debug("FID tracking not supported:", error.message);
      }
    };

    // Initialize tracking
    if (document.readyState === "complete") {
      trackLoadPerformance();
    } else {
      window.addEventListener("load", trackLoadPerformance);
    }

    trackLCP();
    trackCLS();
    trackFID();

    // Cleanup
    return () => {
      window.removeEventListener("load", trackLoadPerformance);
    };
  }, [enabled, deviceInfo]);

  // Toggle visibility with keyboard shortcut (Ctrl+Shift+P)
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (event.ctrlKey && event.shiftKey && event.key === "P") {
        setIsVisible((prev) => !prev);
      }
    };

    document.addEventListener("keydown", handleKeyPress);
    return () => document.removeEventListener("keydown", handleKeyPress);
  }, []);

  if (!enabled || !isVisible) return null;

  const formatTime = (time) => {
    return time ? `${time.toFixed(2)}ms` : "N/A";
  };

  const getPerformanceScore = () => {
    const scores = [];

    if (metrics.largestContentfulPaint < 2500) scores.push(100);
    else if (metrics.largestContentfulPaint < 4000) scores.push(75);
    else if (metrics.largestContentfulPaint < 6000) scores.push(50);
    else scores.push(25);

    if (metrics.cumulativeLayoutShift < 0.1) scores.push(100);
    else if (metrics.cumulativeLayoutShift < 0.25) scores.push(75);
    else if (metrics.cumulativeLayoutShift < 0.5) scores.push(50);
    else scores.push(25);

    if (metrics.firstInputDelay < 100) scores.push(100);
    else if (metrics.firstInputDelay < 300) scores.push(75);
    else if (metrics.firstInputDelay < 500) scores.push(50);
    else scores.push(25);

    return Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
  };

  return (
    <div className="fixed top-4 right-4 bg-black bg-opacity-90 text-white p-4 z-50 max-w-sm">
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-sm font-bold">Performance Monitor</h3>
        <button
          onClick={() => setIsVisible(false)}
          className="text-gray-400 hover:text-white"
        >
          ×
        </button>
      </div>

      <div className="space-y-2 text-xs">
        <div className="flex justify-between">
          <span>Load Time:</span>
          <span>{formatTime(metrics.loadTime)}</span>
        </div>
        <div className="flex justify-between">
          <span>First Paint:</span>
          <span>{formatTime(metrics.firstPaint)}</span>
        </div>
        <div className="flex justify-between">
          <span>FCP:</span>
          <span>{formatTime(metrics.firstContentfulPaint)}</span>
        </div>
        <div className="flex justify-between">
          <span>LCP:</span>
          <span>{formatTime(metrics.largestContentfulPaint)}</span>
        </div>
        <div className="flex justify-between">
          <span>CLS:</span>
          <span>{metrics.cumulativeLayoutShift.toFixed(3)}</span>
        </div>
        <div className="flex justify-between">
          <span>FID:</span>
          <span>{formatTime(metrics.firstInputDelay)}</span>
        </div>

        <div className="border-t border-gray-600 pt-2 mt-2">
          <div className="flex justify-between">
            <span>Performance Score:</span>
            <span
              className={`font-bold ${
                getPerformanceScore() >= 90
                  ? "text-green-400"
                  : getPerformanceScore() >= 70
                  ? "text-yellow-400"
                  : "text-red-400"
              }`}
            >
              {getPerformanceScore()}/100
            </span>
          </div>
        </div>

        <div className="border-t border-gray-600 pt-2 mt-2">
          <div className="text-xs text-gray-400">
            <div>Memory: {deviceInfo.deviceMemory}GB</div>
            <div>Cores: {deviceInfo.hardwareConcurrency}</div>
            <div>
              Connection: {deviceInfo.connection?.effectiveType || "Unknown"}
            </div>
            <div>Low-end: {deviceInfo.isLowEnd ? "Yes" : "No"}</div>
            <div>
              Slow connection: {deviceInfo.isSlowConnection ? "Yes" : "No"}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

PerformanceMonitor.displayName = "PerformanceMonitor";

export default PerformanceMonitor;
